// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import Spinner from '../Spinner';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';

import './PreviewPanel.less';
import './images/fail.png';

/**
 * `PreviewPanel` component.
 */
class PreviewPanel extends Component {
    state = {
        loaded: false,
    };

    componentDidMount() {
        const { html } = this.props;

        if (html && this.iframeRef) {
            this.updateIFrame(html);
        }
    }

    componentWillReceiveProps(nextProps) {
        const { html } = this.props;
        const { html: nextHtml } = nextProps;

        if (nextHtml !== html && nextHtml && this.iframeRef) {
            this.updateIFrame(nextHtml);
        }
    }

    updateIFrame(content) {
        const doc = this.iframeRef.contentDocument || this.iframeRef.contentWindow.document;

        doc.open();
        doc.write(content);
        doc.close();
    }

    renderIFrame() {
        return (
            <iframe
                sandbox="allow-same-origin"
                scrolling="no"
                ref={ref => (this.iframeRef = ref)}
                onLoad={e => {
                    if (!e || !e.target) {
                        return;
                    }

                    const iframe = e.target;
                    const win = iframe.contentWindow;
                    const doc = iframe.contentDocument || win.document;

                    this.setState({ loaded: true }, () => {
                        doc.body.style.display = 'inline-block';
                        doc.body.style.transformOrigin = 'top left';
                        doc.body.style.transform = `scale(${Math.min(win.innerWidth / doc.body.offsetWidth, 1)})`;
                        doc.querySelector('html').scrollTop = 0;
                    });
                }}
            />
        );
    }

    renderLoader() {
        const { baseClassName } = this.props;
        const { loaded } = this.state;

        if (loaded) {
            return null;
        }

        return (
            <div className={`${baseClassName}__content-blocker`}>
                <div className={`${baseClassName}__loader`}>
                    <Spinner className={`${baseClassName}__loader-spinner`} />
                </div>
            </div>
        );
    }

    render() {
        const {
            html,
            background,
            image,
            padding,
            alt,
            children,
            className,
            baseClassName,
            ...props
        } = this.props;
        const { loaded } = this.state;

        let el = null;
        let imageSrc = image;
        const backgroundObj = {};

        if (!image && !background && !html) {
            imageSrc = './images/fail.png';
        }

        if (imageSrc) {
            const paddings = {};
            if (padding) {
                paddings.style = {
                    padding: `${padding instanceof Array ? padding.join('px ') : padding}px`,
                };
            }

            el = <img className={`${baseClassName}__image`} src={image} alt={alt} {...paddings} />;
        } else if (background) {
            backgroundObj.style = {
                backgroundImage: `url("${background}")`,
            };
        } else if (html) {
            el = (
                <div
                    className={classNames(
                        `${baseClassName}__html`,
                        {
                            [`${baseClassName}__html--loading`]: !loaded,
                        },
                    )}
                >
                    {this.renderIFrame()}
                    {this.renderLoader()}
                </div>
            );
        }

        return (
            <div
                className={classNames(
                    baseClassName,
                    {
                        [`${baseClassName}--background`]: background,
                    },
                    className,
                )}
                {...backgroundObj}
                {...props}
            >
                {el}

                <div
                    className={classNames(
                        `${baseClassName}__content`,
                        {
                            [`${baseClassName}__content--empty`]: !children,
                        },
                    )}
                >
                    <div className={`${baseClassName}__content-inner`}>
                        {children}
                    </div>
                </div>
            </div>
        );
    }
}

PreviewPanel.propTypes = {
    /**
     * HTML content. Width of content will be adjusted to fit preview panel.
     */
    html: PropTypes.string,
    /**
     * Background image content.
     */
    background: PropTypes.string,
    /**
     * Image content.
     */
    image: PropTypes.string,
    /**
     * Padding of image content (in pixels). Could be a number or an array of numbers. Behavior as in CSS property `padding`.
     */
    padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number),
    ]),
    /**
     * Alt property for an image.
     */
    alt: PropTypes.string,
    /**
     * Custom content for hover over preview panel. Hidden in card for the row view.
     */
    children: PropTypes.any,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

PreviewPanel.defaultProps = {
    html: null,
    background: null,
    image: null,
    padding: 0,
    alt: null,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}preview-panel`,
};

export default PreviewPanel;



// WEBPACK FOOTER //
// ./components/Card/PreviewPanel.js